import { AlertTitle, Collapse } from "@mui/material"
import { Dispatch, ReactElement, SetStateAction, useState, useRef } from "react"

import SingleApplicationDropzone from "./singleApplicationDropzone"
import useSubmitDraft from "../../../../services/middleware/hooks/draftApplication/useSubmitDraft"
import { IFormData } from "../../../../services/middleware/types/amendApplication/launchAmendmentTypes"
import { IUpload } from "../../../../services/middleware/types/singleApplication/uploadAttachmentTypes"
import AppAlert from "../../../dataDisplay/alerts/appAlert"
import BackNextButtons from "../formGenerator/backNextButtons"

interface IAttachmentArrayProps {
    formKey: string
    minItems: number
    fileUrlKey: string
    imageMode: boolean
    initialUploads?: IUpload[]
    maxItems?: number
    setFormData: Dispatch<SetStateAction<IFormData>>
    setCurrentStepIndex: Dispatch<SetStateAction<number>>
    setSupplementingInfo: Dispatch<
        SetStateAction<{
            [x: string]: unknown
        }>
    >
    isFirstStep: boolean
    onClickBackFirstStep?: () => void
    commissioning?: boolean
}

/** Attachment Array base - context agnostic attachment array */
const AttachmentArrayBase = ({
    formKey,
    minItems,
    fileUrlKey,
    imageMode,
    initialUploads = [],
    maxItems,
    setFormData,
    setCurrentStepIndex,
    setSupplementingInfo,
    isFirstStep,
    onClickBackFirstStep,
    commissioning,
}: IAttachmentArrayProps): ReactElement => {
    const [uploads, setUploads] = useState<IUpload[]>(initialUploads)
    const [displayError, setDisplayError] = useState(false)
    const { postDraftApplication } = useSubmitDraft()
    const formDataRef = useRef<IFormData>({})
    const supplementingInfoRef = useRef<{ [key: string]: unknown }>({})

    const onClickNext = async (): Promise<void> => {
        if (uploads.length < minItems) {
            setDisplayError(true)
        } else if (maxItems && uploads.length > maxItems) {
            setDisplayError(true)
        } else {
            setDisplayError(false)

            // Prepare form values from uploads
            const formValues = uploads.map((upload) => ({
                [fileUrlKey]: upload.fileUrl,
            }))

            // Create the data objects
            const formDataToUpdate = { [formKey]: formValues }
            const supplementingInfoToUpdate = { [formKey]: uploads }

            // Step 1: Save updates to our refs first to ensure we have the latest data
            formDataRef.current = {
                ...formDataRef.current,
                ...formDataToUpdate,
            }
            supplementingInfoRef.current = {
                ...supplementingInfoRef.current,
                ...supplementingInfoToUpdate,
            }
            await postDraftApplication(false, formDataToUpdate)
            setFormData((prev) => ({ ...prev, ...formDataToUpdate }))
            setSupplementingInfo((prev) => ({
                ...prev,
                ...supplementingInfoToUpdate,
            }))
            setCurrentStepIndex((prev) => prev + 1)
        }
    }

    const someUploadsLoading = uploads.some((upload) => upload.loading)

    return (
        <>
            <SingleApplicationDropzone
                uploads={uploads}
                setUploads={setUploads}
                imageMode={imageMode}
                commissioning={commissioning}
            />
            <Collapse in={displayError && uploads.length < minItems}>
                <AppAlert severity="warning">
                    <AlertTitle>
                        {`You must provide at least ${minItems} attachment`}
                    </AlertTitle>
                </AppAlert>
            </Collapse>
            {!!maxItems && (
                <Collapse in={displayError && uploads.length > maxItems}>
                    <AppAlert severity="warning">
                        <AlertTitle>
                            {`You have exceeded the maximum number of permitted attachments (${maxItems})`}
                        </AlertTitle>
                    </AppAlert>
                </Collapse>
            )}

            <BackNextButtons
                onClickNext={onClickNext}
                disabled={someUploadsLoading}
                setCurrentStepIndex={setCurrentStepIndex}
                isFirstStep={isFirstStep}
                onClickBackFirstStep={onClickBackFirstStep}
            />
        </>
    )
}

AttachmentArrayBase.defaultProps = {
    initialUploads: [],
    maxItems: undefined,
    onClickBackFirstStep: undefined,
    commissioning: false,
}

export default AttachmentArrayBase
